import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Button,
  Chip
} from '@mui/material';
import {
  Business,
  People,
  Payment,
  Settings,
  ArrowForward
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const OrganizationOverview: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const [org, setOrg] = useState<{ id: string; name?: string } | null>(null);
  const [memberCount, setMemberCount] = useState<number>(0);
  const [subscription, setSubscription] = useState<{ status?: string; seats?: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!organizationId) return;
      try {
        setLoading(true);
        setError(null);
        const [orgRes, usersRes, subRes] = await Promise.all([
          api.getOrganization(organizationId),
          api.getOrganizationUsers(organizationId),
          api.getSubscription(organizationId).catch(() => ({ subscription: null, subscriptions: [] }))
        ]);
        const orgData = orgRes?.data ?? orgRes;
        setOrg(orgData ? { id: orgData.id || organizationId, name: orgData.name } : { id: organizationId, name: 'Organization' });
        const users = usersRes?.data ?? usersRes ?? [];
        setMemberCount(Array.isArray(users) ? users.length : 0);
        const sub = subRes?.subscription ?? subRes?.data ?? (Array.isArray(subRes) ? subRes[0] : subRes);
        setSubscription(sub ? { status: sub.status, seats: sub.seats } : null);
      } catch (err: any) {
        console.error('Error loading organization overview:', err);
        setError(err.response?.data?.message || 'Failed to load organization overview');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [organizationId]);

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const basePath = `/org/${organizationId}`;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Organization
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Organization</Typography>
              </Box>
              <Typography variant="h5" gutterBottom>
                {org?.name ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overview and settings
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Members</Typography>
              </Box>
              <Typography variant="h4">{memberCount}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Team members in this organization
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate(`${basePath}/users`)}
              >
                Manage users
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Payment color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Subscription</Typography>
              </Box>
              {subscription ? (
                <>
                  <Chip
                    label={subscription.status ?? 'Unknown'}
                    color={subscription.status === 'active' ? 'success' : 'default'}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {subscription.seats != null && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {subscription.seats} seat(s)
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No active subscription
                </Typography>
              )}
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate(`${basePath}/subscriptions`)}
              >
                Subscriptions
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Settings color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Settings</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Organization details and configuration
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate(`${basePath}/settings`)}
              >
                Organization settings
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationOverview;
